import { defineStore } from 'pinia';
import { recipePurchaseApi, type PurchaseCommand, type PurchaseProjection, type PurchaseRequest } from '../api/recipe-purchase';
import { StorefrontApiError } from '../api/storefront';
import { useNutritionSelectionStore } from './nutrition-selection';
import { personalSelectionFeatureEnabled } from '../api/nutrition';
import { menuDraftApi, validMenuRecipe as validRecipe } from '../api/menu-draft';
export interface MenuRecipe { id: string; title: string; image?: string | null }
interface ScopeState {
  projection: PurchaseProjection | null;
  phase: 'idle' | 'loading' | 'ready' | 'writing' | 'stale' | 'unknown' | 'error';
  pending: PurchaseRequest | null; message: string | null; readGeneration: number;
}
type DraftPhase = 'idle' | 'loading' | 'ready' | 'saving' | 'conflict' | 'pending' | 'error';
const newScope = (): ScopeState => ({ projection: null, phase: 'idle', pending: null, message: null, readGeneration: 0 });
export const useRecipePurchaseStore = defineStore('recipe-purchase', {
  state: () => ({ scopes: {} as Record<string, ScopeState>, menu: [] as MenuRecipe[], menuBusy: false,
    menuGeneration: 0, menuDraftVersion: null as number | null, menuDraftPhase: 'idle' as DraftPhase,
    menuDraftMessage: null as string | null, menuDirty: false,
    menuRemovedIds: [] as string[], menuDraftServerIds: [] as string[], menuDraftPending: false, menuDraftUnavailableCount: 0,
    menuDraftLoad: null as Promise<boolean> | null, menuDraftSave: null as Promise<void> | null,
    /** Bumped only by real local menu changes; a menu read started before a change must not restore. */
    menuRevision: 0,
    /** Explicit server/local state, including an empty tombstone, forbids prepared-plan restore. */
    menuExplicit: false }),
  getters: {
    canReducePendingMenu: state => state.menuDraftPhase === 'pending' && (state.menuDirty || state.menuDraftUnavailableCount > 0)
      && state.menu.length < state.menuDraftServerIds.length + state.menuDraftUnavailableCount
      && state.menu.every(r => state.menuDraftServerIds.includes(r.id)),
  },
  actions: {
    resetForBuyer() {
      for (const state of Object.values(this.scopes)) ++state.readGeneration;
      this.scopes = {}; this.menu = []; this.menuBusy = false;
      ++this.menuGeneration; ++this.menuRevision; this.menuExplicit = false;
      this.menuDraftVersion = null; this.menuDraftPhase = 'idle'; this.menuDraftMessage = null;
      this.menuDirty = false; this.menuDraftLoad = null; this.menuDraftSave = null;
      this.menuRemovedIds = []; this.menuDraftServerIds = []; this.menuDraftPending = false; this.menuDraftUnavailableCount = 0;
    },
    async loadMenuDraft(reload = false): Promise<boolean> {
      if (this.menuDraftLoad) return this.menuDraftLoad;
      if (!reload) {
        if (this.menuDraftPhase === 'ready' || this.menuDraftPhase === 'saving') return true;
        if (this.menuDraftPhase !== 'idle') return false;
      } else if (this.menuDraftSave) return false;
      const generation = this.menuGeneration, revision = this.menuRevision;
      // A deliberate reload replaces a known conflicting/unsaved snapshot, but never a newer edit.
      const discard = reload && this.menuDraftVersion !== null;
      this.menuDraftPhase = 'loading'; this.menuDraftMessage = null;
      const loading = (async (): Promise<boolean> => {
        try {
          const draft = await menuDraftApi.read();
          if (generation !== this.menuGeneration) return false;
          this.menuDraftVersion = draft.version;
          this.menuDraftServerIds = draft.recipes.map(r => r.id); this.menuDraftPending = !!draft.pending; this.menuDraftUnavailableCount = draft.unavailable_count ?? 0;
          if (discard && revision === this.menuRevision) { this.menuDirty = false; this.menuRemovedIds = []; }
          if (!this.menuDirty) {
            this.menu = draft.recipes; this.menuExplicit = draft.explicit;
          } else {
            // Additions made before the initial GET must not silently delete older server dishes.
            const local = this.menu;
            const merged = [...draft.recipes.filter(r => !this.menuRemovedIds.includes(r.id) && !local.some(v => v.id === r.id)), ...local];
            if (merged.length > 50) {
              this.menuDraftPhase = 'conflict';
              this.menuDraftMessage = 'Вместе с сохранёнными блюдами меню превышает 50 рецептов. Загрузите меню с сервера и проверьте выбор.';
              return false;
            }
            this.menu = merged;
          }
          if (draft.pending) {
            this.menuDraftPhase = 'pending'; this.menuDraftMessage = 'Объединение меню после входа ещё не завершено. Если вместе больше 50 блюд, уберите часть блюд из этого меню и явно сохраните уменьшенный список, затем загрузите меню снова. Гостевые блюда сервер пока сохраняет отдельно.';
            return false;
          }
          this.menuDraftPhase = 'ready'; return true;
        } catch {
          if (generation === this.menuGeneration) {
            this.menuDraftPhase = 'error'; this.menuDraftMessage = 'Не удалось загрузить меню. Локальный выбор не сохранён; загрузите меню ещё раз.';
          }
          return false;
        }
      })();
      this.menuDraftLoad = loading;
      const ready = await loading;
      if (generation !== this.menuGeneration) return false;
      this.menuDraftLoad = null;
      if (ready && this.menuDirty) void this.persistMenu();
      return ready;
    },
    async persistMenu(reducePending = false): Promise<void> {
      const generation = this.menuGeneration;
      if (this.menuDraftPhase === 'idle' || this.menuDraftPhase === 'loading') {
        if (!await this.loadMenuDraft() || generation !== this.menuGeneration) return;
      }
      if (this.menuDraftSave) return this.menuDraftSave;
      if (!this.menuDirty || this.menuDraftVersion === null
        || (reducePending ? !this.canReducePendingMenu : this.menuDraftPhase !== 'ready' || this.menuDraftPending)) return;
      this.menuDraftPhase = 'saving'; this.menuDraftMessage = null;
      const saving = (async () => {
        try {
          while (this.menuDirty && generation === this.menuGeneration) {
            const revision = this.menuRevision, version = this.menuDraftVersion!;
            const draft = await menuDraftApi.save(version, this.menu.map(r => r.id));
            if (generation !== this.menuGeneration) return;
            if (draft.version <= version || !draft.explicit) throw Error('invalid_menu_draft_ack');
            this.menuDraftVersion = draft.version;
            this.menuDraftServerIds = draft.recipes.map(r => r.id); this.menuDraftPending = !!draft.pending; this.menuDraftUnavailableCount = draft.unavailable_count ?? 0;
            // Acknowledgement may advance CAS, never replace newer local ordering/selection.
            if (revision === this.menuRevision) {
              this.menuDirty = false; this.menuRemovedIds = [];
              this.menu = draft.recipes;
            }
            if (draft.pending) {
              this.menuDraftPhase = 'pending'; this.menuDraftMessage = 'Уменьшенное меню сохранено, но объединение ещё не завершено. Загрузите меню с сервера и при необходимости уберите ещё блюда.'; return;
            }
            // Recovery is one explicitly requested PUT, not permission to flush queued pending edits.
            if (reducePending && this.menuDirty) {
              this.menuDraftPhase = 'error'; this.menuDraftMessage = 'Выбор изменился во время сохранения. Проверьте меню и явно повторите сохранение.'; return;
            }
          }
          if (generation === this.menuGeneration) this.menuDraftPhase = 'ready';
        } catch (error) {
          if (generation !== this.menuGeneration) return;
          const payload = error instanceof StorefrontApiError ? error.payload as { draft?: { pending?: unknown } } | null : null;
          if (error instanceof StorefrontApiError && error.statusCode === 409) {
            this.menuDraftPhase = 'conflict'; this.menuDraftMessage = 'Меню изменилось в другом окне. Локальный выбор не сохранён. Загрузите меню с сервера перед новыми изменениями.';
          } else if (payload?.draft?.pending) {
            this.menuDraftPending = true;
            this.menuDraftPhase = 'pending'; this.menuDraftMessage = 'Ожидаем объединения меню. Проверьте меню повторной загрузкой.';
          } else {
            this.menuDraftPhase = 'error'; this.menuDraftMessage = 'Не удалось подтвердить сохранение меню. Локальный выбор сохранён только на этой странице.';
          }
        }
      })();
      this.menuDraftSave = saving;
      await saving;
      if (generation !== this.menuGeneration) return;
      this.menuDraftSave = null;
      // The awaited saving closure may have changed the phase from 'saving'.
      if (this.menuDirty && (this.menuDraftPhase as DraftPhase) === 'ready') void this.persistMenu();
    },
    async reloadMenuDraft(): Promise<void> {
      const generation = this.menuGeneration;
      await this.loadMenuDraft(true);
      if (generation === this.menuGeneration) await this.read('menu');
    },
    async reducePendingMenuDraft(): Promise<void> {
      if (!this.canReducePendingMenu) return;
      if (!this.menuDirty && this.menuDraftUnavailableCount > 0) {
        ++this.menuRevision; this.menuExplicit = true; this.menuDirty = true;
      }
      const generation = this.menuGeneration, revision = this.menuRevision;
      await this.persistMenu(true);
      if (generation === this.menuGeneration && revision === this.menuRevision && !this.menuDirty
        && (this.menuDraftPhase === 'ready' || this.menuDraftPhase === 'pending')) await this.reloadMenuDraft();
    },
    async retryMenuDraft(): Promise<void> {
      if (this.menuDraftPhase !== 'error' || this.menuDraftVersion === null || this.menuDraftPending) return;
      // Explicit retry uses the SAME expected version: a lost successful PUT will conflict, not overwrite.
      this.menuDraftPhase = 'ready'; await this.persistMenu();
    },
    menuChanged() { ++this.menuRevision; this.menuExplicit = true; this.menuDirty = true; void this.persistMenu(); },
    scope(id: string): ScopeState {
      if (!this.scopes[id]) this.scopes[id] = newScope();
      return this.scopes[id];
    },
    addRecipe(recipe: MenuRecipe) {
      const valid = validRecipe(recipe);
      if (valid && !this.menuDraftPending && this.menu.length < 50 && !this.menu.some(r => r.id === valid.id)) {
        this.menuRemovedIds = this.menuRemovedIds.filter(id => id !== valid.id);
        this.menu.push(valid); this.menuChanged();
      }
    },
    removeRecipe(id: string) {
      if (!this.menu.some(r => r.id === id)) return;
      if (!this.menuRemovedIds.includes(id)) this.menuRemovedIds.push(id);
      this.menu = this.menu.filter(r => r.id !== id); this.menuChanged();
    },
    moveRecipe(id: string, delta: number) {
      const index = this.menu.findIndex(r => r.id === id), target = index + delta;
      if (index < 0 || target < 0 || target >= this.menu.length) return;
      const [recipe] = this.menu.splice(index, 1); this.menu.splice(target, 0, recipe); this.menuChanged();
    },
    async read(id: string): Promise<void> {
      const ownerGeneration = this.menuGeneration;
      if (id === 'menu') await this.loadMenuDraft();
      if (ownerGeneration !== this.menuGeneration) return;
      const s = this.scope(id);
      if (s.phase === 'writing') return;
      const frozen = s.pending !== null, generation = ++s.readGeneration, menuRevision = this.menuRevision;
      if (!frozen) s.phase = 'loading';
      try {
        const p = await recipePurchaseApi.read(id);
        if (generation !== s.readGeneration || this.scopes[id] !== s) return;
        s.projection = p;
        // Prepared server menu is durable; restore the dish list after reload when no local draft exists.
        if (id === 'menu' && this.menuDraftPhase === 'ready' && p.plan_id !== 'unprepared' && !this.menuExplicit && menuRevision === this.menuRevision && !this.menu.length && p.selections.length && p.selections.length <= 50) {
          const restored = p.selections.map(v => validRecipe({ id: v.collectionId, title: v.title || v.collectionId }));
          if (restored.every(Boolean) && new Set(restored.map(r => r!.id)).size === restored.length) { this.menu = restored.map(r => ({ id: r!.id, title: r!.title })); this.menuChanged(); }
        }
        // A GET cannot acknowledge an operation whose HTTP response was lost.
        if (!frozen) { s.phase = 'ready'; s.message = null; }
      } catch {
        if (generation !== s.readGeneration) return;
        if (!frozen) { s.phase = 'error'; s.message = 'Не удалось проверить покупку. Попробуйте загрузить ещё раз.'; }
      }
    },
    async command(id: string, type: PurchaseCommand, payload: Record<string, unknown>): Promise<boolean> {
      const s = this.scope(id), p = s.projection;
      if (id === 'menu' && (this.menuDraftPhase !== 'ready' || this.menuDirty)) return false;
      if (!p || s.phase !== 'ready' || s.pending || p.history_lost || p.provenance_conflict || p.pending_guest_plan) return false;
      const request: PurchaseRequest = { selectionId: id, type, payload: JSON.parse(JSON.stringify(payload)), expectedPlanVersion: p.plan_version, expectedCartVersion: p.cart_version, operationId: crypto.randomUUID() };
      s.pending = request; s.phase = 'writing'; s.message = null; ++s.readGeneration;
      try {
        const r = await recipePurchaseApi.execute(request);
        if (this.scopes[id] !== s) return false;
        s.projection = r.projection;
        if (r.status !== 'applied') { s.phase = 'stale'; s.message = 'Покупка изменилась. Проверьте актуальные данные перед новым действием.'; return false; }
        s.pending = null; s.phase = 'ready';
        if (type === 'applySelection') s.message = 'Продукты добавлены в корзину. Другие позиции сохранены.';
        return true;
      } catch (error) {
        if (this.scopes[id] !== s) return false;
        if (error instanceof StorefrontApiError && error.statusCode >= 400 && error.statusCode < 500) {
          const body = error.payload as { projection?: PurchaseProjection; status?: string; message?: string } | null;
          if (body?.projection?.selection_id === id) s.projection = body.projection;
          const unknown = body?.status === 'operation_result_unknown';
          s.phase = unknown ? 'unknown' : 'stale';
          s.message = unknown ? 'Результат операции неизвестен. Не повторяем добавление: проверьте корзину.'
            : typeof body?.message === 'string' && /[А-Яа-я]/.test(body.message) ? body.message
            : 'Действие отклонено. Обновите покупку и проверьте условия подбора.';
          // Definitive rejection permits a read, never an automatic write retry.
          if (!unknown) s.pending = null;
        } else {
          s.phase = 'unknown'; s.message = 'Результат операции неизвестен. Не повторяем добавление: проверьте корзину.';
        }
        return false;
      }
    },
    async prepareRecipe(recipeId: string, id = `recipe_${recipeId}`): Promise<void> {
      await this.read(id);
      const s = this.scope(id);
      if (s.phase === 'ready' && s.projection?.plan_id === 'unprepared') await this.command(id, 'prepareSelection', { collectionId: recipeId });
    },
    async prepareMenu(): Promise<void> {
      if (this.menuBusy || !this.menu.length) return;
      this.menuBusy = true;
      const generation = this.menuGeneration;
      try {
        await this.loadMenuDraft(); await this.persistMenu();
        if (generation !== this.menuGeneration || this.menuDraftPhase !== 'ready' || this.menuDirty || !this.menu.length) return;
        const entries = this.menu.map(r => ({ collectionId: r.id })), revision = this.menuRevision;
        await this.read('menu');
        if (generation !== this.menuGeneration || revision !== this.menuRevision) return;
        const p = this.scope('menu').projection;
        if (!p || this.scope('menu').phase !== 'ready') return;
        if (p.plan_id === 'unprepared') { await this.command('menu', 'prepareSelection', { entries }); return; }
        for (const old of p.selections) {
          if (generation !== this.menuGeneration || revision !== this.menuRevision) return;
          if (!entries.some(r => r.collectionId === old.collectionId) && !await this.command('menu', 'removeSelection', { slotId: old.slotId })) return;
        }
        for (const entry of entries) {
          if (generation !== this.menuGeneration || revision !== this.menuRevision) return;
          if (!this.scope('menu').projection?.selections.some(r => r.collectionId === entry.collectionId) && !await this.command('menu', 'addSelection', entry)) return;
        }
      } finally { if (generation === this.menuGeneration) this.menuBusy = false; }
    },
    async apply(id: string): Promise<boolean> {
      const s = this.scope(id), p = s.projection;
      if (s.phase !== 'ready' || s.pending || !p || !p.selections.length || p.unresolved.length) return false;
      // Serialize the complete warning flow too, not just the eventual HTTP mutation.
      s.phase = 'writing';
      try {
        if (personalSelectionFeatureEnabled()) for (const line of p.lines.filter(l => l.qty > 0)) {
          const outcome = await useNutritionSelectionStore().checkBeforeAdd(line.item_id, line.title);
          if (this.scopes[id] !== s) return false;
          if (outcome !== 'allow' && outcome !== 'feature_off') {
            s.message = 'Добавление не подтверждено по условиям подбора.'; return false;
          }
        }
      } catch {
        s.message = 'Не удалось проверить условия подбора. Корзина не изменена.'; return false;
      } finally { s.phase = 'ready'; }
      if (this.scopes[id] !== s) return false;
      return this.command(id, 'applySelection', {});
    },
  },
});
